import { useLayoutEffect, useState } from "react";
import * as RechartsPrimitive from "recharts";
import { PieSectorDataItem } from "recharts/types/polar/Pie";

import {
  ChartConfig,
  ChartContainer,
  useChartContext,
} from "./chart-container";
import {
  circleToPath,
  createNestedCircles,
  getBaseRadiusForCircles,
  getBaseTextPosition,
  getEvenLines,
  getIconPosition,
  getLablePositionBasedOnTagTextPosition,
  getLines,
  getRectBoxPositionBaseOnTextPosition,
  getSinCos,
  getTextAnchor,
} from "./utils";

interface Data {
  domain: string;
  slice: number;
  fill: string;
  value: number;
  dashedConnector?: boolean;
}

export type LabelProps = PieSectorDataItem & {
  domain: string | undefined;
  layout: "md" | "lg";
  data: Data[];
  widthScale?: number;
  containerHeight: number;
  containerWidth: number;
  labelOffset?: number;
  onLabelClick?: (domain: string) => void;
};

// export const Gradients: React.FC<{ data: Data[] }> = ({ data }) => {
//   return (
//     <defs>
//       {data.map((item, i) => {
//         const { x1, y1, x2, y2 } = angleToSvgVector(135);
//
//         return (
//           <linearGradient
//             id={`grad-${item.domain}`}
//             key={item.domain}
//             x1={x1}
//             y1={y1}
//             x2={x2}
//             y2={y2}
//           >
//             <stop offset="0%" stopColor={item.gradient.start} />
//             <stop offset="100%" stopColor={item.gradient.end} />
//           </linearGradient>
//         );
//       })}
//     </defs>
//   );
// };

const CIRCLES_COUNT = 3;

export const RenderValue: React.FC<{
  dynamicOuterRadius: number;
  innerRadius: number;
  cx: number;
  cy: number;
  midAngle: number;
  value: number | string | undefined;
  domain: string | undefined;
}> = ({ dynamicOuterRadius, cx, cy, midAngle, value, innerRadius, domain }) => {
  const RAD = Math.PI / 180;

  const dynamicValueRadius = dynamicOuterRadius - 14;
  const minValueRadius = innerRadius + 14;
  const valueRadius =
    dynamicValueRadius <= minValueRadius ? minValueRadius : dynamicValueRadius;

  const x = cx + valueRadius * Math.cos(-midAngle * RAD);
  const y = cy + valueRadius * Math.sin(-midAngle * RAD);

  return (
    <text
      data-id={`chart-value-${domain}`}
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={14}
      fill="#23252A"
      fontFamily={"Fixel Text"}
      fontWeight={"400"}
    >
      {value}
    </text>
  );
};

export const RenderCircles: React.FC<LabelProps> = (props) => {
  const {
    cx,
    cy,
    outerRadius,
    innerRadius,
    startAngle,
    endAngle,
    widthScale,
    data,
    fill,
    domain,
  } = props ?? {};
  if (cx === undefined || cy === undefined || outerRadius === undefined)
    return null;

  const { sin, cos } = getSinCos(startAngle ?? 0);

  const { sx, sy, mx, my } = getEvenLines({
    cx,
    cy,
    outerRadius,
    labelOffset: 1,
    innerRadius: innerRadius ?? 0,
    sin,
    cos,
    widthScale: widthScale ?? 1,
    startAngle,
  });

  const baseRadius = getBaseRadiusForCircles(outerRadius, CIRCLES_COUNT);

  const circles = createNestedCircles(
    cx,
    cy,
    CIRCLES_COUNT,
    baseRadius,
    baseRadius,
  );

  const circlesPath = circles.map((c) => circleToPath(c)).join(" ");

  const isDashed = data.find((item) => item.domain === domain)?.dashedConnector;
  return (
    <>
      <RechartsPrimitive.Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}`}
        stroke="#CDD3DC"
        strokeWidth={2}
        fill="none"
        {...(isDashed && { strokeDasharray: "5 5" })}
      />
      <path d={circlesPath} stroke="#CDD3DC" fill="none" strokeWidth={2} />
    </>
  );
};

export const RenderLabel: React.FC<LabelProps> = (props) => {
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle = 0,
    endAngle,
    fill,
    domain,
    data,
    widthScale = 1,
    containerHeight,
    containerWidth,
    labelOffset = 120,
    onLabelClick,
  } = props;

  const { config } = useChartContext();

  const [tagLayout, setTagLayout] = useState({ height: 0, width: 0 });
  const [labelLayout, setLableLayout] = useState({ height: 0, width: 0 });

  const [tagEl, setTagEl] = useState<SVGTextElement | null>(null);
  const [labelEl, setLabelEl] = useState<SVGTextElement | null>(null);

  useLayoutEffect(() => {
    const tagBBox = tagEl?.getBBox();
    const labelBBox = labelEl?.getBBox();

    if (tagBBox) setTagLayout(tagBBox);
    if (labelBBox) setLableLayout(labelBBox);
  }, [labelEl, tagEl]);

  if (!midAngle) return null;
  if (!outerRadius) return null;
  if (!cx || !cy) return null;
  if (!fill) return null;
  if (!domain) return null;
  if (!config) return null;

  const { sin, cos } = getSinCos(startAngle ?? 0);

  const textAnchor = getTextAnchor({ cos });

  const { sx, sy, mx, my } = getLines({
    cx,
    cy,
    outerRadius,
    labelOffset,
    innerRadius: innerRadius ?? 0,
    sin,
    cos,
    widthScale: widthScale ?? 1,
    startAngle,
    containerHeight,
    containerWidth,
  });

  const { textX, textY } = getBaseTextPosition({
    cx,
    cy,
    midAngle,
    outerRadius,
    labelOffset,
    textWidth: tagLayout.width,
  });

  const { labelX, labelY } = getLablePositionBasedOnTagTextPosition({
    textX,
    textY,
    width: labelLayout.width - tagLayout.width - 8,
    textAnchor,
  });

  const { rectX, rectY, rectWidth, rectHeight } =
    getRectBoxPositionBaseOnTextPosition({
      textX,
      textY,
      textWidth: tagLayout.width,
      textHeight: tagLayout.height,
      paddingHorizontal: 8,
      paddingVertical: 1,
      textAnchor,
    });

  const baseRadius = getBaseRadiusForCircles(outerRadius, CIRCLES_COUNT);

  const circles = createNestedCircles(
    cx,
    cy,
    CIRCLES_COUNT,
    baseRadius,
    baseRadius,
  );

  const circlesPath = circles.map((c) => circleToPath(c)).join(" ");

  const isDashed = data.find((item) => item.domain === domain)?.dashedConnector;

  const iconPosition = getIconPosition({
    textAnchor,
    labelX,
    labelY,
    labelWidth: labelLayout.width,
  });

  return (
    <>
      <g>
        <RechartsPrimitive.Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />

        <path
          d={`M${sx},${sy}L${mx},${my}`}
          stroke="#CDD3DC"
          strokeWidth={2}
          fill="none"
          {...(isDashed && { strokeDasharray: "5 5" })}
        />

        <path d={circlesPath} stroke="#CDD3DC" fill="none" strokeWidth={2} />

        <rect
          fill={config[domain]?.color + "33"}
          width={rectWidth}
          height={rectHeight}
          rx={9}
          ry={9}
          x={rectX}
          y={rectY}
        />
      </g>
      <a
        onClick={() => onLabelClick?.(domain)}
        style={{ cursor: onLabelClick ? "pointer" : "default" }}
      >
        <svg
          data-id={`chart-icon-${domain}`}
          {...iconPosition}
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="30"
          fill="none"
          viewBox="0 0 30 30"
        >
          <path
            fill="#fff"
            d="M.625 8.625a8 8 0 0 1 8-8h12a8 8 0 0 1 8 8v12a8 8 0 0 1-8 8h-12a8 8 0 0 1-8-8z"
          ></path>
          <path
            fill="#CDD3DC"
            d="M8.625.625v.625h12V0h-12zm20 8H28v12h1.25v-12zm-8 20V28h-12v1.25h12zm-20-8h.625v-12H0v12zm8 8V28a7.375 7.375 0 0 1-7.375-7.375H0a8.625 8.625 0 0 0 8.625 8.625zm20-8H28A7.375 7.375 0 0 1 20.625 28v1.25a8.625 8.625 0 0 0 8.625-8.625zm-8-20v.625A7.375 7.375 0 0 1 28 8.625h1.25A8.625 8.625 0 0 0 20.625 0zm-12 0V0A8.625 8.625 0 0 0 0 8.625h1.25A7.375 7.375 0 0 1 8.625 1.25z"
          ></path>
          <path
            stroke="#000"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.25"
            d="M14.625 20.345a5.3 5.3 0 0 0-6.243-.933c-.57.304-1.257-.07-1.257-.716V11.12c0-.53.245-1.04.677-1.348a5.304 5.304 0 0 1 6.823.573 5.304 5.304 0 0 1 6.823-.573c.432.308.677.818.677 1.348v7.576c0 .645-.687 1.02-1.257.716a5.3 5.3 0 0 0-6.243.933"
          ></path>
          <path
            stroke="#23252A"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.25"
            d="M14.625 20.341v-10"
          ></path>
        </svg>
        <text
          data-id={`chart-label-${domain}`}
          x={labelX}
          y={labelY}
          ref={setLabelEl}
          dy={-20}
          textAnchor={textAnchor}
          fontFamily="Fixel Text"
          fontWeight={500}
          fontSize={16}
          fill="#011916"
        >
          {config[domain]?.label}
        </text>
      </a>
      <text
        data-id={`chart-tag-${domain}`}
        ref={setTagEl}
        x={textX}
        y={textY}
        textAnchor={textAnchor}
        fill={config[domain]?.color}
        fontFamily="Fixel Text"
        fontWeight={400}
        fontSize={12}
      >
        {config[domain]?.tag}
      </text>
    </>
  );
};

export { ChartContainer, type ChartConfig };
